import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    E,
    T,
    balancesManager,
    positionsManager,
    ordersManager,
    useUser,
    httpClient,
    wsClient
} from '../imports'

interface SnaptradeContextType {
    accounts: T.BrokerageCredentials[];
    activeAccountId: string | null;
    isConnecting: boolean;
    isLoading: boolean;
    isActivating: boolean;
    isResettingPaper: boolean;
    refreshAccounts: () => Promise<void>;
    startConnection: () => Promise<void>;
    activateAccount: (accountId: string) => Promise<void>;
    activatePaperAccount: (startingCash: number) => Promise<void>;
    resetPaperAccount: (startingCash: number) => Promise<void>;
    onConnectionSuccess: (() => void) | null;
    setOnConnectionSuccess: (callback: (() => void) | null) => void;
}

const SnaptradeContext = createContext<SnaptradeContextType | undefined>(undefined);

interface SnaptradeProviderProps {
    children: ReactNode;
}

export const SnaptradeProvider: React.FC<SnaptradeProviderProps> = ({ children }) => {
    const [accounts, setAccounts] = useState<T.BrokerageCredentials[]>([]);
    const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [isResettingPaper, setIsResettingPaper] = useState(false);
    const [onConnectionSuccess, setOnConnectionSuccess] = useState<(() => void) | null>(null);
    const { userData } = useUser();

    // Fetch accounts on mount if user is authenticated
    // Skip if we're in a popup window (callback page)
    useEffect(() => {
        if (userData.userId && !window.opener) {
            // console.log('[SnaptradeCtx] User data available, fetching accounts');
            // Small delay to ensure token provider is initialized
            const timer = setTimeout(() => {
                refreshAccounts();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [userData]);

    const refreshAccounts = async () => {
        if (!userData) return;
        const previousActiveAccountId = activeAccountId;
        
        setIsLoading(true);
        try {
            const response = await httpClient.getSnaptradeAccounts();

            let newAccounts: T.BrokerageCredentials[] = [];
            if (response.success && response.data) {
                newAccounts = response.data as T.BrokerageCredentials[];
                // Sort accounts alphabetically by brokerage name
                newAccounts.sort((a, b) => a.brokerage.localeCompare(b.brokerage));
                setAccounts(newAccounts);
            } else {
                setAccounts([]);
            }

            const activeResponse = await httpClient.getActiveSnaptradeAccount();
            if (activeResponse.success && activeResponse.data) {
                const activeId = activeResponse.data as string;
                setActiveAccountId(activeId);

                const activeInList = newAccounts.some((account) => account.brokerAccountId === activeId);
                if (!activeInList && activeId.startsWith('paper-')) {
                    setAccounts((prev) => {
                        const hasPaper = prev.some((account) => account.brokerAccountId === activeId);
                        if (hasPaper) return prev;
                        return [
                            {
                                userId: userData.userId,
                                brokerage: E.Brokerage.PAPER,
                                brokerAccountId: activeId,
                                accountName: 'Paper Trading Account',
                                apiKey: activeId
                            },
                            ...prev
                        ];
                    });
                }

                const accountSwitched = previousActiveAccountId !== activeId;
                if ((accountSwitched || !balancesManager.isConnected) && userData.userId) {
                    await initializePortfolioSession(userData.userId);
                }
            } else {
                setActiveAccountId(null);
            }
        } catch (error: any) {
            // Silently handle token provider not ready error
            if (error?.message?.includes('Token provider not initialized')) {
                console.log('[SnaptradeCtx] Token provider not ready yet, will retry when profile updates');
            } else {
                console.error('[SnaptradeCtx] Failed to fetch accounts:', error);
            }
            setAccounts([]);
        } finally {
            setIsLoading(false);
        }
    };

    const activatePaperAccount = async (startingCash: number) => {
        setIsActivating(true);
        try {
            const response = await httpClient.activatePaperAccount(startingCash);
            if (!response.success) {
                throw new Error(response.message || 'Failed to activate paper account');
            }

            await refreshAccounts();
        } catch (error: any) {
            throw error;
        } finally {
            setIsActivating(false);
        }
    };

    const resetPaperAccount = async (startingCash: number) => {
        setIsResettingPaper(true);
        try {
            const response = await httpClient.resetPaperAccount(startingCash);
            if (!response.success) {
                throw new Error(response.message || 'Failed to reset paper account');
            }

            if (userData?.userId) {
                await initializePortfolioSession(userData.userId);
            }
        } catch (error: any) {
            throw error;
        } finally {
            setIsResettingPaper(false);
        }
    };

    const activateAccount = async (accountId: string) => {
        setIsActivating(true);
        try {
            const response = await httpClient.activateSnaptradeAccount(accountId);
            if (response.success) {
                setActiveAccountId(accountId);
                
                // Initialize portfolio session via WebSocket with all three managers
                if (userData?.userId) {
                    await initializePortfolioSession(userData.userId);
                } else {
                    console.warn('[SnaptradeCtx] Cannot initialize portfolio session, userData userId missing');
                }
            } else {
                throw new Error(response.message || 'Failed to activate account');
            }
        } catch (error: any) {
            throw error;
        } finally {
            setIsActivating(false);
        }
    };

    /**
     * Initialize WebSocket connection and all three managers.
     * Called after active account is confirmed.
     */
    const initializePortfolioSession = async (userId: string) => {
        try {
            // Connect WebSocket first
            await wsClient.connectWs(userId);
            
            // Initialize all three managers in parallel
            await Promise.all([
                balancesManager.init(),
                positionsManager.init(),
                ordersManager.init()
            ]);
        } catch (error) {
            console.error('[SnaptradeCtx] Failed to initialize portfolio session:', error);
            throw error;
        }
    };

    const startConnection = async () => {
        try {
            const portalUrl = await httpClient.getSnaptradeConnectionPortalUrl();
            
            // Open in centered popup
            const width = 600;
            const height = 700;
            const left = (window.screen.width / 2) - (width / 2);
            const top = (window.screen.height / 2) - (height / 2);
            
            const popup = window.open(
                portalUrl,
                'SnapTrade Connection',
                // `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
            );

            if (!popup) {
                throw new Error('Popup blocked. Please allow popups for this site.');
            }

            setIsConnecting(true);

            // Start polling for connection
            startConnectionPolling(popup);

        } catch (error: any) {
            throw error;
        }
    };

    const startConnectionPolling = (popup: Window) => {
        // Listen for messages from popup
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'SNAPTRADE_CONNECTION_SUCCESS') {
                clearInterval(closedCheckInterval);
                window.removeEventListener('message', handleMessage);
                // Refresh accounts from server
                refreshAccounts();
                setIsConnecting(false);
            } else if (event.data?.type === 'SNAPTRADE_CONNECTION_ERROR') {
                clearInterval(closedCheckInterval);
                window.removeEventListener('message', handleMessage);
                
                setIsConnecting(false);
                console.error('[SnaptradeCtx] Connection failed:', event.data.error);
            }
        };

        window.addEventListener('message', handleMessage);

        // Check if popup closed every second (to end connecting state)
        const closedCheckInterval = setInterval(() => {
            if (popup.closed) {
                window.removeEventListener('message', handleMessage);
                clearInterval(closedCheckInterval);
                setIsConnecting(false);
            }
        }, 1000);
    };

    const value: SnaptradeContextType = {
        accounts,
        activeAccountId,
        isConnecting,
        isLoading,
        isActivating,
        isResettingPaper,
        refreshAccounts,
        startConnection,
        activateAccount,
        activatePaperAccount,
        resetPaperAccount,
        onConnectionSuccess,
        setOnConnectionSuccess
    };

    return (
        <SnaptradeContext.Provider value={value}>
            {children}
        </SnaptradeContext.Provider>
    );
};

export const useSnaptrade = (): SnaptradeContextType => {
    const context = useContext(SnaptradeContext);
    if (!context) {
        throw new Error('useSnaptrade must be used within SnaptradeProvider');
    }
    return context;
};
